import { useMemo } from "react";
import { useAuth } from "@/features/auth/auth.context";

export interface ShellUser {
  name: string;
  email: string;
  /** Una o dos letras. Lo que pinta el avatar. */
  initials: string;
  /**
   * El rol del sistema. El armazón lo usa para UNA cosa: enseñarle al
   * super-admin el conmutador de vistas de panel (Anahí, 27-ago). No es
   * la autorización — esa vive en el servidor y en las guardas.
   */
  role: string;
}

/**
 * Iniciales a partir del nombre. Dos palabras → dos letras; una → una.
 * Nunca vacío: un avatar en blanco parece un fallo de carga.
 */
export function initialsOf(name: string, email: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.slice(0, 2).map((word) => word.charAt(0));
  const initials = letters.join("").toUpperCase();
  if (initials) return initials;
  return (email.trim().charAt(0) || "?").toUpperCase();
}

export interface ShellSession {
  user: ShellUser | null;
  logout: () => void;
}

/**
 * El único punto del armazón que sabe de sesión.
 *
 * ── Por qué está aquí y no en props ──────────────────────────────────
 * `RootLayout` tiene que quedarse en "la lista de navegación y nada
 * más", así que el armazón no puede pedirle la persona conectada por
 * props. Y el resto de componentes de esta carpeta (`AppSidebar`,
 * `UserMenu`, `NavDrawer`) SÍ son puros: reciben `user` y `onLogout`, y
 * por eso se montan en una prueba sin levantar el contexto de sesión.
 *
 * Esta es entonces la ÚNICA frontera entre `shared/ui` y `features/`, a
 * propósito y en un solo archivo. Si mañana la sesión cambia de sitio, se
 * cambia aquí y ningún componente de presentación se entera.
 */
export function useShellUser(): ShellSession {
  const { user, logout } = useAuth();

  return useMemo(() => {
    if (!user) return { user: null, logout: () => void logout() };

    const name = user.fullName || `${user.firstName} ${user.lastName}`.trim();
    return {
      user: {
        name,
        email: user.email,
        initials: initialsOf(name, user.email),
        role: user.role,
      },
      logout: () => void logout(),
    };
  }, [user, logout]);
}
